import type {
  DownloadShippingLabelRequest,
  DownloadShippingLabelResponse,
  ShippingLabelItem,
} from '@/infrastructure/ShippingClient';

export interface PreparedShippingLabelDownload {
  blob: Blob;
  fileName: string;
}

type DownloadShippingLabel = (request: DownloadShippingLabelRequest) => Promise<DownloadShippingLabelResponse>;

export const fetchFedExShippingLabel = async (
  downloadShippingLabel: DownloadShippingLabel,
  orderId: string,
  providerOrderId: string,
): Promise<ShippingLabelItem> => {
  const response = await downloadShippingLabel({
    provider: 'FEDEX',
    orderId,
    providerOrderId,
  });

  if (!response.success) {
    throw new Error(response.message || 'Failed to download FedEx label');
  }

  const label = response.labels?.[0];
  if (!label) {
    throw new Error('FedEx label data is missing');
  }

  return label;
};

const safeFileName = (label: ShippingLabelItem): string => {
  const serverFileName = label.fileName?.split(/[\\/]/).pop()?.trim();
  if (serverFileName) {
    return serverFileName;
  }

  const safeProviderOrderId = label.providerOrderId.replace(/[^A-Za-z0-9._-]/g, '_');
  return `${safeProviderOrderId}.${label.format === 'ZPLII' ? 'zpl' : 'pdf'}`;
};

export const prepareShippingLabelDownload = (label: ShippingLabelItem): PreparedShippingLabelDownload => {
  const encodedLabel = label.labelData ?? label.pdfData;
  if (!encodedLabel) {
    throw new Error('Label data is missing');
  }

  const binaryLabel = atob(encodedLabel);
  const labelBytes = Uint8Array.from(binaryLabel, (character) => character.charCodeAt(0));
  const contentType = label.contentType ?? (label.format === 'ZPLII' ? 'application/octet-stream' : 'application/pdf');

  return {
    blob: new Blob([labelBytes], { type: contentType }),
    fileName: safeFileName(label),
  };
};

export const downloadShippingLabelFile = (label: ShippingLabelItem): string => {
  const preparedLabel = prepareShippingLabelDownload(label);
  const labelUrl = URL.createObjectURL(preparedLabel.blob);
  const downloadLink = document.createElement('a');
  downloadLink.href = labelUrl;
  downloadLink.download = preparedLabel.fileName;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();
  URL.revokeObjectURL(labelUrl);
  return preparedLabel.fileName;
};
